<template>
    <div>
        <div id="change-language" class="d-flex flex-column shadow-sm">
            <div class="d-flex flex-column flag-list">
                <a v-for="(item, index) in languages" :key="index"
                   :href="`${item.url}?redirect=${TENANT_BASE_URL}interview-form/${id}/detail?eid=${employeeId}`"
                   class="flag-item" :title="item.title">
                    <img :src="getFlagUrl(item.key)" :alt="item.title" class="flag-icon-img" />
                </a>
            </div>
        </div>

        <modal
            id="apply-form-modal"
            size="extra-large"
            v-model="showFormModal"
            :loading="loading"
            :preloader="preloader"
            :title="$t('apply')"
            :alignModal="'top'"
            :hideFooter="true"
            :modalBackdrop="false"
        >
            <form ref="form" :data-url='`${TENANT_BASE_URL}/interview-form/${id}`' @submit.prevent="submitData">
                <app-form-wizard ref="wizard" :tabs="wizardTabs" :enable-all="true"/>
            </form>
        </modal>

        <div>
            <a :href="urlGenerator(`/job-list`)" class="btn btn-primary mr-2">{{ $t('list_job') }}</a>
            <button type="button" class="btn btn-warning" @click="showFormModal = true">{{ $t('apply') }}</button>
        </div>
    </div>
</template>

<script>
import FormHelperMixins from "../../../../../common/Mixin/Global/FormHelperMixins";
import {TENANT_BASE_URL} from "../../../../../common/Config/UrlHelper";
import {formDataAssigner} from "../../../../../common/Helper/Support/FormHelper";
import {formatDateForServer} from "../../../../../common/Helper/Support/DateTimeHelper";
import {normalizeLatinInput} from "../../../../../common/Helper/Support/TextHelper";
import {axiosGet} from "../../../../../common/Helper/AxiosHelper";
import {LANGUAGES} from "../../../../../common/Config/apiUrl";
import CandidateProfileStep from "./Steps/CandidateProfileStep";
import CandidateShiftStep from "./Steps/CandidateShiftStep";
import CandidateConfirmStep from "./Steps/CandidateConfirmStep";

export default {
    name  : "CandidateForm",
    mixins: [FormHelperMixins],
    props : {
        id          : {
            type: String
        },
        employeeId  : {
            type: String
        },
        showLanguage: {
            type: String
        }
    },
    data() {
        return {
            TENANT_BASE_URL,
            showFormModal: false,
            locate       : window?.appLanguage.toLowerCase(),
            showModal    : this.showLanguage === 'true',
            preloader    : this.showLanguage === 'true',
            languages    : [],
            files        : [],
            fields       : [],
            timeList     : [],
            formData     : {
                job_broker   : this.employeeId,
                name_romaji  : '',
                name_katakana: '',
            },
        }
    },
    watch: {
        showFormModal(value) {
            if (!value) {
                this.resetFormState();
            }
        },
        'formData.name_romaji'(value) {
            this.normalizeCandidateField('name_romaji', value);
        },
        'formData.name_katakana'(value) {
            this.normalizeCandidateField('name_katakana', value);
        }
    },
    computed: {
        wizardTabs() {
            return [
                {
                    name     : this.$t('my_profile'),
                    component: CandidateProfileStep,
                    props    : this.profileStepProps
                },
                {
                    name     : this.$t('shift'),
                    component: CandidateShiftStep,
                    props    : this.shiftStepProps
                },
                {
                    name     : this.$t('confirm'),
                    component: CandidateConfirmStep,
                    props    : this.confirmStepProps
                }
            ]
        },
        profileStepProps() {
            return {
                formData     : this.formData,
                errors       : this.errors || {},
                tenantBaseUrl: this.TENANT_BASE_URL,
                files        : this.files,
                onFilesChange: this.updateFiles,
                fields       : this.fields,
                locate       : this.locate,
                normalizeLatinInput,
                validateStep : (fields) => this.validateStepByFields(fields),
            }
        },
        shiftStepProps() {
            return {
                formData     : this.formData,
                errors       : this.errors || {},
                timeList     : this.timeList,
                workPlaceId  : this.id,
                validateStep : (fields) => this.validateStepByFields(fields),
            }
        },
        confirmStepProps() {
            return {
                formData: this.formData,
                fields  : this.fields,
                files   : this.files,
                timeList: this.timeList,
                locate  : this.locate
            }
        },
    },
    mounted() {
        axiosGet(LANGUAGES).then(({data}) => {
            this.languages = data
            this.preloader = false
        });

        axiosGet(`${TENANT_BASE_URL}api/candidate/time`).then(({data}) => {
            this.timeList = data
        }).catch((error) => {
            this.$toastr.e(error.response.data.message)
        }).finally(() => {
            this.errors = {};
        })
        axiosGet(`${TENANT_BASE_URL}api/attribute/fields/candidate`).then(({data}) => {
            if (data.length > 0) {
                for (const field of data) {
                    this.formData[field.name] = '';
                }
            }
            this.fields = data
        }).catch((error) => {
            this.$toastr.e(error.response.data.message)
        })
    },
    methods: {
        normalizeCandidateField(field, value) {
            const normalizedValue = normalizeLatinInput(value);

            if (normalizedValue !== value) {
                this.$set(this.formData, field, normalizedValue);
            }
        },
        sanitizeCandidateNames() {
            this.formData.name_romaji = normalizeLatinInput(this.formData.name_romaji || '');
            this.formData.name_katakana = normalizeLatinInput(this.formData.name_katakana || '');
        },
        updateFiles(value) {
            this.files = value;
        },
        buildFormData(validateOnly = false) {
            this.sanitizeCandidateNames();
            const formattedStart = this.formData.start ? formatDateForServer(this.formData.start) : '';
            const payload = {...this.formData, start: formattedStart};
            const formData = formDataAssigner(new FormData, payload);

            this.files.forEach(file => formData.append('files[]', file));
            formData.set('validate_only', validateOnly);

            if (this.fields.length > 0) {
                for (const field of this.fields) {
                    formData.set(field.name, this.formData[field.name]);
                }
            }

            return formData;
        },
        async validateStepByFields(stepFields = []) {
            const url = this.$refs.form.dataset["url"];
            const formData = this.buildFormData(true);
            this.loading = true;

            try {
                await this.axiosPost({
                    url,
                    data: formData,
                });
                this.errors = {};
                return true;
            } catch (error) {
                const responseErrors = error?.response?.data?.errors;
                this.errors = responseErrors || {};

                if (!responseErrors) {
                    if (this.$toastr && this.$toastr.e) {
                        this.$toastr.e(this.$t('something_went_wrong'));
                    }
                    return false;
                }

                const hasStepErrors = stepFields.some(field => {
                    return Object.keys(responseErrors).some(errorKey => (
                        errorKey === field || errorKey.startsWith(`${field}.`)
                    ));
                });

                if (!hasStepErrors) {
                    this.errors = {};
                    return true;
                }

                return false;
            } finally {
                this.loading = false;
            }
        },
        submitData() {
            this.preloader      = true;

            let url      = this.$refs.form.dataset["url"];
            let formData = this.buildFormData();

            this.submitFromFixin('post', url, formData);
        },
        afterSuccess({data}) {
            this.preloader = false;
            this.formData  = {};
            this.showFormModal = false;
            $('.modal-backdrop').hide();
            this.$emit('input', false);
            this.$toastr.s(this.$t('Candidate created successfully!'))
        },
        afterSuccessFromGetEditData(response) {
            this.preloader = false;
            this.formData  = response.data;
        },
        afterFinalResponse() {
            this.preloader = false;
        },
        getFlagUrl(key) {
            const flagMap = {
                'en': 'gb',
                'ja': 'jp',
                'zh': 'cn',
                'cs': 'cz',
                'da': 'dk',
                'el': 'gr',
                'ko': 'kr',
                'sq': 'al',
                'he': 'il',
                'ar': 'sa',
                'vi': 'vn'
            };
            const country = flagMap[key] || key;
            return `https://flagcdn.com/w40/${country}.png`;
        },
        resetFormState() {
            this.errors = {};
            this.files = [];
            this.formData  = {};
        },
    },
}
</script>

<style lang="scss" scoped>
#change-language {
    top: 70px;
    right: 0;
    z-index: 10;
    position: fixed;

    .btn {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
        border-bottom-left-radius: 0;
        width: 42px;
        height: 38px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 0;
    }

    .flag-list {
        background: #fff;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        
        .flag-item {
            display: block;
            width: 42px;
            height: 30px;
            margin-bottom: 0;
            
            .flag-icon-img {
                width: 100%;
                height: 100%;
                object-fit: cover;
                display: block;
                border: 1px solid #ddd;
                border-top: none;
                border-right: none;
            }
        }
    }
}
</style>
